<template>
  <v-navigation-drawer
    v-model="open"
    app
    right
    temporary
    width="400"
    class="assistant-drawer"
  >
    <div class="assistant-panel d-flex flex-column">
      <div class="d-flex align-center px-4 py-3 blue darken-2 white--text">
        <v-icon color="white" class="mr-2">mdi-robot-outline</v-icon>
        <div class="text-subtitle-1 font-weight-medium">Assistant</div>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              dark
              v-bind="attrs"
              v-on="on"
              aria-label="New chat"
              :disabled="!canStartNewChat"
              @click="resetChat"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>New chat</span>
        </v-tooltip>
        <v-btn icon small dark @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div ref="thread" class="assistant-thread flex-grow-1 px-4 py-3">
        <div v-if="!messages.length" class="grey--text text-body-2 assistant-empty">
          <p class="mb-2">
            Ask about your profile, projects, hours, absences, holidays, colleagues, equipment, notifications, skills, or IT support.
            You can also ask about HR policies, benefits, and how-to instructions (sports compensation, refunds, and similar procedures).
            To create a vacation, half-day vacation (including multi-day), or sick leave (including 0.5 day or several half-days), you will need to confirm it in the panel.
          </p>
          <div class="text-caption mb-1">Examples:</div>
          <ul class="assistant-empty-examples">
            <li
              v-for="example in emptyExamples"
              :key="example"
            >
              <button
                type="button"
                class="assistant-empty-example"
                :disabled="busy || sessionEnded"
                @click="useExample(example)"
              >{{ example }}</button>
            </li>
          </ul>
        </div>
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="mb-3"
          :class="msg.role === 'user' ? 'text-right' : 'text-left'"
        >
          <div
            class="assistant-bubble d-inline-block text-left"
            :class="msg.role === 'user' ? 'assistant-bubble--user' : 'assistant-bubble--assistant'"
          >
            <div v-if="msg.status" class="text-caption grey--text mb-1">{{ msg.status }}</div>
            <div
              v-if="msg.role === 'assistant'"
              class="text-body-2 assistant-markdown"
              v-html="renderMarkdown(msg.content)"
            ></div>
            <div
              v-else
              class="text-body-2 assistant-plain"
            >{{ msg.content }}</div>
            <div
              v-if="msg.proposal && !msg.proposal.resolved"
              class="assistant-proposal mt-2"
            >
              <div class="text-caption grey--text mb-2">{{ proposalLabel(msg.proposal) }}</div>
              <div class="d-flex">
                <v-btn
                  small
                  color="primary"
                  :loading="msg.proposal.busy"
                  :disabled="busy && !msg.proposal.busy"
                  @click="confirmProposal(msg)"
                >Confirm</v-btn>
                <v-btn
                  small
                  text
                  class="ml-2"
                  :disabled="busy || msg.proposal.busy"
                  @click="cancelProposal(msg)"
                >Cancel</v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="sessionEnded"
        class="assistant-session-ended px-4 pb-2"
      >
        <div class="text-caption grey--text mb-2">{{ sessionEndedCopy }}</div>
        <v-btn
          color="blue darken-2"
          dark
          block
          small
          @click="resetChat"
        >New chat</v-btn>
      </div>

      <div class="px-4 pb-4 pt-2">
        <v-textarea
          v-model="draft"
          rows="2"
          auto-grow
          hide-details
          outlined
          dense
          placeholder="Ask the assistant..."
          :disabled="busy || sessionEnded"
          @keydown.enter.exact.prevent="send"
        ></v-textarea>
        <v-btn
          class="mt-2"
          color="blue darken-2"
          dark
          block
          :loading="busy"
          :disabled="!draft.trim() || busy || sessionEnded"
          @click="send"
        >
          Send
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { streamAssistantChat, confirmAbsenceRequest } from '@/services/assistant'
import { renderAssistantMarkdown } from '@/utils/assistantMarkdown'

export default {
  name: 'AssistantPanel',
  data () {
    return {
      draft: '',
      busy: false,
      messages: [],
      sessionEnded: false,
      sessionEndedReason: '',
      emptyExamples: [
        'Who is my supervisor and what department am I in?',
        'Which projects am I assigned to?',
        'How many hours did I report this month?',
        'How many vacation and sick-leave days do I have left?',
        'Show my pending absence requests.',
        'What are the company holidays this year?',
        'Find a colleague by name and show their profile.',
        'What equipment is assigned to me?',
        'Show my recent notifications.',
        'What skills are on my profile?',
        'Who are the IT support contacts?',
        'How do I apply for sports compensation?',
        'Create a vacation from next Monday to Friday.',
        'Create a half-day vacation tomorrow from 10:00.',
        'Create a sick-leave request for today, 0.5 day.'
      ]
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    canStartNewChat () {
      return (this.messages.length > 0 || this.sessionEnded) && !this.busy
    },
    sessionEndedCopy () {
      if (this.sessionEndedReason === 'submitted') {
        return 'Request submitted. Start a new chat to continue.'
      }
      return 'Draft cancelled. Start a new chat to continue.'
    },
    open: {
      get () {
        return this.$store.getters.getAssistantPanel.show
      },
      set (show) {
        this.$store.dispatch('showAssistantPanel', { show })
      }
    }
  },
  watch: {
    messages () {
      this.$nextTick(() => {
        const el = this.$refs.thread
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    sessionEnded () {
      this.$nextTick(() => {
        const el = this.$refs.thread
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  },
  methods: {
    renderMarkdown (content) {
      return renderAssistantMarkdown(content)
    },
    useExample (example) {
      if (this.busy || this.sessionEnded) return
      this.draft = example
    },
    proposalLabel (proposal) {
      if (!proposal) return ''
      if (proposal.summary) return proposal.summary
      const typeLabel = proposal.type === 'sick_leave' ? 'Sick leave' : 'Vacation'
      const dates = [proposal.startDate, proposal.endDate].filter(Boolean).join(' – ')
      const confirmer = proposal.confirmerName ? `, confirmer ${proposal.confirmerName}` : ''
      return `${typeLabel} ${dates}${confirmer}`
    },
    historyForApi () {
      return this.messages
        .filter((m) => m.role === 'user' || (m.role === 'assistant' && m.content))
        .map((m) => ({ role: m.role, content: m.content }))
    },
    resetChat () {
      if (this.busy) return
      this.messages.forEach((msg) => {
        if (msg && msg.proposal && !msg.proposal.resolved) {
          this.$set(msg.proposal, 'resolved', true)
        }
      })
      this.messages = []
      this.draft = ''
      this.busy = false
      this.sessionEnded = false
      this.sessionEndedReason = ''
    },
    endSession (reason) {
      this.sessionEnded = true
      this.sessionEndedReason = reason
    },
    async send () {
      const text = this.draft.trim()
      if (!text || this.busy || this.sessionEnded) return
      this.draft = ''
      this.messages.push({ role: 'user', content: text })
      const assistantMsg = { role: 'assistant', content: '', status: 'Thinking...' }
      this.messages.push(assistantMsg)
      this.busy = true
      try {
        await streamAssistantChat({
          token: this.user.token,
          messages: this.historyForApi(),
          onEvent: (event) => {
            if (event.event === 'tool_start') {
              assistantMsg.status = 'Looking up data...'
            } else if (event.event === 'token' && event.text) {
              assistantMsg.status = ''
              assistantMsg.content += event.text
            } else if (event.event === 'done') {
              assistantMsg.status = ''
              if (!assistantMsg.content && event.content) {
                assistantMsg.content = event.content
              }
            } else if (event.event === 'error') {
              assistantMsg.status = ''
              assistantMsg.content = event.message || 'Something went wrong.'
            } else if (event.event === 'proposal' && event.proposalId) {
              this.$set(assistantMsg, 'proposal', {
                proposalId: event.proposalId,
                type: event.type,
                startDate: event.startDate,
                endDate: event.endDate,
                duration: event.duration,
                confirmerName: event.confirmerName,
                summary: event.summary,
                resolved: false,
                busy: false
              })
            }
            this.messages = this.messages.slice()
          }
        })
        if (!assistantMsg.content) {
          assistantMsg.content = 'No answer returned.'
        }
      } catch (err) {
        assistantMsg.status = ''
        assistantMsg.content = err.message || 'Assistant request failed.'
      } finally {
        this.busy = false
      }
    },
    async confirmProposal (msg) {
      if (!msg || !msg.proposal || msg.proposal.resolved || this.busy) return
      this.busy = true
      this.$set(msg.proposal, 'busy', true)
      this.messages = this.messages.slice()
      try {
        const result = await confirmAbsenceRequest({
          token: this.user.token,
          proposalId: msg.proposal.proposalId
        })
        this.$set(msg.proposal, 'resolved', true)
        this.$set(msg.proposal, 'busy', false)
        const warning = result && result.emailWarning
        const requestId = result && result.requestId
        this.messages.push({
          role: 'assistant',
          content: warning
            ? `Request #${requestId} submitted. ${warning}`
            : `Request #${requestId} submitted.`
        })
        this.endSession('submitted')
      } catch (err) {
        this.$set(msg.proposal, 'busy', false)
        if (err.status === 400 || err.status === 403 || err.status === 404) {
          this.$set(msg.proposal, 'resolved', true)
          this.endSession('cancelled')
        }
        this.messages.push({
          role: 'assistant',
          content: err.message || 'Failed to submit absence request.'
        })
      } finally {
        this.busy = false
      }
    },
    cancelProposal (msg) {
      if (!msg || !msg.proposal || msg.proposal.resolved || this.busy) return
      this.$set(msg.proposal, 'resolved', true)
      this.messages.push({
        role: 'assistant',
        content: 'Cancelled. The request was not submitted.'
      })
      this.endSession('cancelled')
    }
  }
}
</script>

<style scoped>
.assistant-drawer {
  z-index: 1005 !important;
}
.assistant-panel {
  height: 100%;
}
.assistant-thread {
  overflow-y: auto;
  min-height: 0;
}
.assistant-empty p {
  margin: 0 0 8px;
}
.assistant-empty-examples {
  margin: 0;
  padding-left: 1.1em;
}
.assistant-empty-examples li {
  margin: 0.2em 0;
}
.assistant-empty-example {
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.assistant-empty-example:hover,
.assistant-empty-example:focus {
  color: #1565c0;
}
.theme--dark .assistant-empty-example:hover,
.theme--dark .assistant-empty-example:focus {
  color: #90caf9;
}
.assistant-empty-example:disabled {
  cursor: default;
  text-decoration: none;
}
.assistant-session-ended {
  flex-shrink: 0;
}
.assistant-bubble {
  max-width: 92%;
  border-radius: 8px;
  padding: 8px 12px;
}
.assistant-bubble--user {
  background: #e3f2fd;
}
.assistant-bubble--assistant {
  background: #f5f5f5;
}
.theme--dark .assistant-bubble--user {
  background: rgba(25, 118, 210, 0.35);
}
.theme--dark .assistant-bubble--assistant {
  background: rgba(255, 255, 255, 0.08);
}
.assistant-plain {
  white-space: pre-wrap;
}
.assistant-markdown >>> p {
  margin: 0 0 0.5em;
}
.assistant-markdown >>> p:last-child {
  margin-bottom: 0;
}
.assistant-markdown >>> ul,
.assistant-markdown >>> ol {
  margin: 0.25em 0 0.5em;
  padding-left: 1.25em;
}
.assistant-markdown >>> li {
  margin: 0.15em 0;
}
.assistant-markdown >>> strong {
  font-weight: 600;
}
.assistant-markdown >>> code {
  font-size: 0.9em;
  padding: 0.1em 0.35em;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.06);
}
.theme--dark .assistant-markdown >>> code {
  background: rgba(255, 255, 255, 0.1);
}
.assistant-markdown >>> pre {
  margin: 0.5em 0;
  padding: 8px;
  overflow-x: auto;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
}
.theme--dark .assistant-markdown >>> pre {
  background: rgba(255, 255, 255, 0.08);
}
.assistant-markdown >>> pre code {
  padding: 0;
  background: transparent;
}
.assistant-markdown >>> a {
  color: #1565c0;
  word-break: break-all;
}
.theme--dark .assistant-markdown >>> a {
  color: #90caf9;
}
</style>
